import { AfterViewChecked, ChangeDetectorRef, inject, Injectable, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EyePasswordTextings } from 'src/app/interfaces/views.interface';
import { safelyDetectChanges, unsubscribeAll } from '../common/app.helpers';
import { FormValidationService } from '../forms/form-validation.service';
import { FormGroupControls } from '../interfaces/form-and-field.interface';
import { ValidationService } from '../services/validation.service';
import { AuthService } from './auth.service';

@Injectable()
export abstract class AuthShared<T> implements OnInit, OnDestroy, AfterViewChecked {
  public oldPasswordErrorText = '';
  public repeatPasswordTextError = '';

  public form: FormGroupControls<T> | null = null;

  protected subscriptions$: Subscription[] = [];

  protected authService = inject(AuthService);
  protected validationService = inject(ValidationService);
  protected changeDetector = inject(ChangeDetectorRef);
  protected formFormValidationService = inject(FormValidationService);

  public ngOnInit(): void {
    this.checkIsAuthenticated();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public ngAfterViewChecked(): void {
    safelyDetectChanges(this.changeDetector);
  }

  public texting: (dataTestId: string, text: string) => EyePasswordTextings = (dataTestId: string, text: string) => ({
    dataTestId,
    text,
  });

  public isFieldValid(controlName: string): boolean {
    return this.form.get(controlName).valid && this.form.get(controlName).touched;
  }

  public getErrorDescriptionId(type: string): string {
    return this.validationService.getErrorDescriptionId(type);
  }

  public isFieldInvalid(controlName: string): boolean {
    return this.validationService.isFieldInvalid(this.form, controlName);
  }

  public isPasswordFieldInvalid(controlName: string): boolean {
    const isInvalid: boolean = this.validationService.isFieldInvalid(this.form, controlName);
    if (isInvalid) {
      this.updatePasswordValidationError(controlName);
    }
    return isInvalid;
  }

  public isRepeatPasswordFieldInvalid(): boolean {
    const isInvalid: boolean = this.validationService.isFieldInvalid(this.form, 'repeatPassword');
    this.updateRepeatPasswordValidationError();
    return isInvalid;
  }

  public isRepeatPasswordFieldValid(): boolean {
    const passwordControl: AbstractControl = this.form.get('password');
    const repeatPasswordControl: AbstractControl = this.form.get('repeatPassword');
    return (
      repeatPasswordControl.valid &&
      repeatPasswordControl.touched &&
      passwordControl.value &&
      !repeatPasswordControl.hasError('required') &&
      !this.form.hasError('passwordsNotMatch')
    );
  }

  protected abstract initializeForm(): void;

  protected validateAllFields(): void {
    this.form.markAllAsTouched();
    this.form.markAsPristine();
  }

  /**
   * @description Subscribes on repeatPassword control changes and marks it as touched.
   *              Needed for correct validation.
   * @protected
   */
  protected validateRepeatPasswordChanges(): void {
    this.subscriptions$.push(
      this.form.get('repeatPassword').valueChanges.subscribe(() => {
        const repeatPasswordControl: AbstractControl = this.form.get('repeatPassword');
        repeatPasswordControl.markAllAsTouched();
        this.updateRepeatPasswordValidationError();
      }),
    );
  }

  protected validatePasswordChanges(controlName: string): void {
    const passwordControl: AbstractControl = this.form.get(controlName);
    this.subscriptions$.push(
      passwordControl.valueChanges.subscribe(() => {
        this.updatePasswordValidationError(controlName);
      }),
    );
  }

  protected checkIsAuthenticated(): void {
    if (!this.authService.isAuthenticated()) {
      this.initializeForm();
    }
  }

  protected updateRepeatPasswordValidationError(): void {
    const repeatPasswordControl: AbstractControl = this.form.get('repeatPassword');
    const repeatPasswordValidationError = this.formFormValidationService.getValidationError(repeatPasswordControl);

    if (this.form.errors && Object.keys(this.form.errors)[0] == 'passwordsNotMatch' && !!repeatPasswordControl.value) {
      this.repeatPasswordTextError = 'REPEAT_PASSWORD_INPUT_ERROR';
    } else if (repeatPasswordValidationError == 'FIELD_REQUIRED_ERROR') {
      this.repeatPasswordTextError = repeatPasswordValidationError;
    } else {
      this.repeatPasswordTextError = '';
    }
  }

  protected updatePasswordValidationError(controlName: string): void {
    this.oldPasswordErrorText = this.formFormValidationService.getValidationError(this.form.get(controlName));
  }
}
